import { useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Button, Form, Input, Layout, Menu, Modal, Space, message } from "antd";
import {
  HomeOutlined,
  LockOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  ShoppingOutlined,
} from "@ant-design/icons";
import { changePassword } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { MERCHANT_BRAND_NAME, MERCHANT_LOGO_URL } from "@/constants/brand";

const { Header, Sider, Content } = Layout;

export default function MerchantLayout() {
  const { user, logout, setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [openKeys, setOpenKeys] = useState<string[]>(() =>
    location.pathname.startsWith("/orders") ? ["orders"] : []
  );
  const [form] = Form.useForm<{ oldPassword: string; newPassword: string; confirm: string }>();

  const displayName = user?.displayName || user?.account || "商户";

  const selectedKeys = useMemo(() => {
    if (location.pathname.startsWith("/orders/payment")) return ["payment-orders"];
    return ["home"];
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname.startsWith("/orders")) {
      setOpenKeys((keys) => (keys.includes("orders") ? keys : [...keys, "orders"]));
    }
  }, [location.pathname]);

  const onLogout = async () => {
    await logout();
    message.success("已退出登录");
    navigate("/login", { replace: true });
  };

  const onChangePassword = async (values: { oldPassword: string; newPassword: string }) => {
    setSubmitting(true);
    try {
      await changePassword(values.oldPassword, values.newPassword);
      setUser(null);
      message.success("密码已修改，请重新登录");
      setPwdOpen(false);
      navigate("/login", { replace: true });
    } catch (err) {
      message.error(err instanceof Error ? err.message : "修改密码失败");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout className="merchant-shell">
      <Sider
        collapsible
        collapsed={collapsed}
        trigger={null}
        width={220}
        className="merchant-sider"
      >
        <div className="merchant-sider__brand">
          <img src={MERCHANT_LOGO_URL} alt={MERCHANT_BRAND_NAME} className="merchant-sider__logo" />
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={selectedKeys}
          openKeys={openKeys}
          onOpenChange={setOpenKeys}
          items={[
            {
              key: "home",
              icon: <HomeOutlined />,
              label: "首页",
              onClick: () => navigate("/"),
            },
            {
              key: "orders",
              icon: <ShoppingOutlined />,
              label: "订单管理",
              children: [
                {
                  key: "payment-orders",
                  label: "支付订单",
                  onClick: () => navigate("/orders/payment"),
                },
              ],
            },
          ]}
        />
      </Sider>

      <Layout>
        <Header className="merchant-header">
          <div className="merchant-header__left">
            <button
              type="button"
              className="merchant-header__toggle"
              onClick={() => setCollapsed((v) => !v)}
              aria-label={collapsed ? "展开菜单" : "收起菜单"}
            >
              {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            </button>
            <span className="merchant-header__welcome">欢迎进入商户系统</span>
          </div>
          <Space size="middle" className="merchant-header__right">
            <span className="merchant-header__user">欢迎您，{displayName}</span>
            <Button type="link" icon={<LockOutlined />} onClick={() => setPwdOpen(true)}>
              修改密码
            </Button>
            <Button type="link" danger icon={<LogoutOutlined />} onClick={() => void onLogout()}>
              退出登录
            </Button>
          </Space>
        </Header>

        <Content className="merchant-content">
          <Outlet />
        </Content>
      </Layout>

      <Modal
        title="修改密码"
        open={pwdOpen}
        onCancel={() => setPwdOpen(false)}
        footer={null}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" onFinish={onChangePassword}>
          <Form.Item
            name="oldPassword"
            label="当前密码"
            rules={[{ required: true, message: "请输入当前密码" }]}
          >
            <Input.Password autoComplete="current-password" />
          </Form.Item>
          <Form.Item
            name="newPassword"
            label="新密码"
            rules={[
              { required: true, message: "请输入新密码" },
              { min: 6, message: "至少 6 位" },
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
          <Form.Item
            name="confirm"
            label="确认新密码"
            dependencies={["newPassword"]}
            rules={[
              { required: true, message: "请再次输入新密码" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("newPassword") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error("两次输入不一致"));
                },
              }),
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
          <Button type="primary" htmlType="submit" block loading={submitting}>
            确认修改
          </Button>
        </Form>
      </Modal>
    </Layout>
  );
}
